// src/components/ClientMissionsCard.jsx
// Point d'entrée client vers « Mes missions ». N'apparaît QUE si le conseiller a partagé
// des missions actives (via la RPC sécurisée get_client_missions). Sinon : rien.
// Quand une action attend le client (devis à accepter, transfert à confirmer), une carte
// d'action renvoie directement au bon panneau de « Mon conseiller ».
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";

const COPY = {
  fr: {
    title: "À faire de votre côté",
    quote: "Un devis attend votre réponse", transfer: "Confirmez l’envoi de votre collection",
    quoteCta: "Voir le devis", transferCta: "Confirmer l’envoi",
  },
  en: {
    title: "Waiting for you",
    quote: "A quote is waiting for your answer", transfer: "Confirm the transfer of your collection",
    quoteCta: "View the quote", transferCta: "Confirm sending",
  },
  de: {
    title: "Ihre Antwort wird erwartet",
    quote: "Ein Angebot wartet auf Ihre Antwort", transfer: "Übertragung Ihrer Sammlung bestätigen",
    quoteCta: "Angebot ansehen", transferCta: "Versand bestätigen",
  },
};

const OPEN_STATUSES = ["accepted", "in_progress", "waiting_client", "ready_to_deliver"];

export default function ClientMissionsCard() {
  const { t, i18n } = useTranslation();
  const tx = COPY[String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2)] || COPY.fr;
  const [count, setCount] = useState(0);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data }, { data: space }] = await Promise.all([
          supabase.rpc("get_client_missions"),
          supabase.rpc("get_client_advisor_space"),
        ]);
        if (!alive) return;
        setCount((data || []).filter((m) => !["closed", "cancelled"].includes(m.status)).length);

        const todo = [];
        for (const d of space?.dossiers || []) {
          for (const m of d.missions || []) {
            if (m.quote?.status === "sent") {
              todo.push({ id: m.id, title: m.title, kind: "quote" });
            } else if (m.mission_type === "collection_transfer" && m.collection_id && m.quote_accepted && OPEN_STATUSES.includes(m.status)) {
              todo.push({ id: m.id, title: m.title, kind: "transfer" });
            }
          }
        }
        setActions(todo);
      } catch {
        /* silencieux : pas de mission = pas de carte */
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!count && !actions.length) return null;

  return (
    <>
      {actions.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
          <div className="mb-3 text-base font-semibold text-gray-950">{tx.title}</div>
          <div className="flex flex-col gap-2">
            {actions.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">{a.kind === "quote" ? tx.quote : tx.transfer}</div>
                  <div className="truncate text-xs text-gray-500">{a.title}</div>
                </div>
                <Link
                  to={`/mon-conseiller?mission=${a.id}&open=${a.kind}`}
                  className="shrink-0 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700"
                >
                  {a.kind === "quote" ? tx.quoteCta : tx.transferCta} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {count > 0 && (
        <Link
          to="/mes-missions"
          className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
        >
          <div className="flex items-center gap-4">
            <span className="text-3xl" aria-hidden>🗂️</span>
            <div>
              <div className="text-base font-semibold text-gray-950">{t("clientMissions.title")}</div>
              <p className="mt-1 text-sm text-gray-600">
                {t("clientMissions.active", { count })}
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white">{t("clientMissions.view")} →</span>
        </Link>
      )}
    </>
  );
}
